import { useState, useEffect, useCallback, useRef } from 'react';
import './ReceiverConfigForm.css';
import RequiresAviotrackBanner from './RequiresAviotrackBanner';
import {
  getReceiverConfig,
  updateReceiverConfig,
  getReceiverConfigSchema,
  pushReceiverUpdate,
  setReceiverAutoUpdate,
} from '../utils/httpApi';
import { isApiSuccess, getApiErrors } from '../utils/api';
import { formatRelativeTime } from '../utils/dateTime';

// While an update runs, poll rather than reading once. The receiver reports its phase over
// minutes on a slow uplink, and a single read cannot show any of it.
const UPDATE_POLL_INTERVAL_MS = 3000;
// Give up after this. An update that dies silently (receiver drops off mid-download) must not
// leave the page polling for ever; the phase simply stops updating and the last one stands.
const UPDATE_POLL_WINDOW_MS = 10 * 60 * 1000;

/**
 * Turn a receiver phase string into something a human reads.
 *
 * The receiver sends terse tokens ("downloading 40%", "verifying", "staged 1.05.09",
 * "restarting 1.05.09") because they travel up the same socket as Beast frames. Expanding them
 * here keeps the wire format small and the wording changeable without a receiver release.
 */
function describePhase(phase) {
  if (!phase) return 'working…';
  const p = String(phase);
  if (p.startsWith('checking')) return 'checking for a newer build…';
  if (p.startsWith('downloading')) {
    const pct = p.match(/(\d+)%/);
    return pct ? `downloading… ${pct[1]}%` : 'downloading…';
  }
  if (p.startsWith('verifying')) return 'verifying download…';
  if (p.startsWith('staged')) return 'installing…';
  if (p.startsWith('restarting')) return 'restarting…';
  return p;
}

/**
 * Schema-driven form for the receiver's remotely-pushable config.
 *
 * The shape of the form is decided by the backend's
 * ReceiverConfigService.getReceiverConfigSchema response — adding a field
 * to the schema there auto-shows it here, no FE change needed.
 *
 * The form only sends the keys the user actually changed (a partial JSON
 * patch); the receiver merges that on top of its current YAML.
 */
/**
 * @param {boolean} [isAviotrack]  false when the receiver has never sent STATS — the
 *   remote-config form is meaningless then (nothing on the device listens), so a
 *   "requires Aviotrack software" banner replaces it. Undefined = unknown, render as before.
 * @param {string|null} [latestReceiverVersion]  for the banner text
 * @param {function} [onNavigate]  App navigation (Downloads link)
 */
function ReceiverConfigForm({ receiverUuid, token, isAviotrack, latestReceiverVersion = null, onNavigate = null, declaredReceiverType = null }) {
  const [schema, setSchema] = useState(null);
  // serverConfig: the persisted operator "desired" patch in receiver_config.
  // currentConfig: the receiver-reported FULL effective config (CFG-CURRENT).
  // Pre-population uses currentConfig when available so the form shows what's
  // actually running on the device; falls back to serverConfig for legacy
  // receivers (pre-1.04.60) that don't push CFG-CURRENT.
  const [serverConfig, setServerConfig] = useState({});
  const [currentConfig, setCurrentConfig] = useState(null);
  const [serverMeta, setServerMeta] = useState({
    desiredVersion: 0,
    appliedVersion: 0,
    appliedStatus: null,
    appliedError: null,
    appliedAt: null,
    receiverConnected: false,
    updatedAt: null,
    updatedBy: null,
    // Live snapshot from the receiver's most recent STATS push.
    detectedDeviceType: null,
    softwareName: null,
    softwareVersion: null,
    // Software-update state (from backend release check + receiver UPDATE-ACK).
    latestVersion: null,
    updateAvailable: false,
    autoUpdate: false,
    installerUpdateRequired: false,
    minInstallerVersion: null,
    updateStatus: null,
    updateError: null,
    updateDetail: null,
    currentGainMode: null,
    currentGainDb: null,
    sampleRateSps: null,
    centerFreqHz: null,
    lastStatsAt: null,
    // When the DEVICE last reported the config it is actually running (CFG-CURRENT).
    // Persisted server-side since V085, so it survives the receiver going offline.
    currentConfigAt: null,
    // Host identity (hardware / OS / memory).
    hostName: null,
    hostModel: null,
    osName: null,
    osVersion: null,
    kernel: null,
    arch: null,
    memTotalMb: null,
    cpuCount: null,
  });
  const [formValues, setFormValues] = useState({});
  // Keys the operator has edited since the last successful push / Reset. loadAll() re-runs
  // while the page is open (every 3 s during an update poll, and whenever the access token
  // refreshes), and each run used to replace formValues wholesale, so whatever was being typed
  // snapped back to the server value mid-edit ("I cannot reconfigure the JVM heap"). A ref, not
  // state: loadAll reads it, and making it a dependency would re-trigger the load on every key.
  const dirtyKeys = useRef(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [savingAutoUpdate, setSavingAutoUpdate] = useState(false);
  const [errors, setErrors] = useState([]);
  const [info, setInfo] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({ DSP: true });

  const loadAll = useCallback(async () => {
    if (!token || !receiverUuid) return;
    setLoading(true);
    setErrors([]);
    try {
      const [schemaResp, configResp] = await Promise.all([
        getReceiverConfigSchema(token),
        getReceiverConfig(receiverUuid, token),
      ]);
      if (isApiSuccess(schemaResp)) {
        setSchema(schemaResp);
      } else {
        setErrors(getApiErrors(schemaResp));
      }
      if (isApiSuccess(configResp)) {
        let parsed = {};
        try { parsed = configResp.configJson ? JSON.parse(configResp.configJson) : {}; }
        catch (_) { parsed = {}; }
        setServerConfig(parsed);
        // Receiver's full effective config from CFG-CURRENT, if any. Used as
        // the form's pre-population source; merged with the desired patch so
        // a queued-but-not-yet-applied value shows in the field.
        let parsedCurrent = null;
        try {
          parsedCurrent = configResp.currentConfigJson
            ? JSON.parse(configResp.currentConfigJson) : null;
        } catch (_) { parsedCurrent = null; }
        setCurrentConfig(parsedCurrent);
        const baseline = parsedCurrent || {};
        // Layer: receiver's current state, then operator's queued patch
        // on top (so a pending CFG-PUSH appears in the form). User edits
        // overlay both.
        setFormValues(prev => withEdits(toFormValues({ ...baseline, ...parsed }), prev, dirtyKeys.current));
        setServerMeta({
          desiredVersion: configResp.desiredVersion || 0,
          appliedVersion: configResp.appliedVersion || 0,
          appliedStatus: configResp.appliedStatus,
          appliedError: configResp.appliedError,
          appliedAt: configResp.appliedAt,
          receiverConnected: !!configResp.receiverConnected,
          // Tri-state, because the boolean above cannot distinguish "offline"
          // from "this backend process hasn't heard from it yet". See the
          // connectionState docs on GetReceiverConfigResponse.
          connectionState: configResp.connectionState,
          backendUptimeMs: configResp.backendUptimeMs,
          lastActiveAt: configResp.lastActiveAt,
          updatedAt: configResp.updatedAt,
          updatedBy: configResp.updatedBy,
          detectedDeviceType: configResp.detectedDeviceType,
          softwareName: configResp.softwareName,
          softwareVersion: configResp.softwareVersion,
          latestVersion: configResp.latestVersion,
          updateAvailable: !!configResp.updateAvailable,
          autoUpdate: !!configResp.autoUpdate,
          installerUpdateRequired: !!configResp.installerUpdateRequired,
          minInstallerVersion: configResp.minInstallerVersion,
          updateStatus: configResp.updateStatus,
          updateError: configResp.updateError,
          updateDetail: configResp.updateDetail,
          currentGainMode: configResp.currentGainMode,
          currentGainDb: configResp.currentGainDb,
          sampleRateSps: configResp.sampleRateSps,
          centerFreqHz: configResp.centerFreqHz,
          lastStatsAt: configResp.lastStatsAt,
          currentConfigAt: configResp.currentConfigAt,
          hostName: configResp.hostName,
          hostModel: configResp.hostModel,
          osName: configResp.osName,
          osVersion: configResp.osVersion,
          kernel: configResp.kernel,
          arch: configResp.arch,
          memTotalMb: configResp.memTotalMb,
          cpuCount: configResp.cpuCount,
        });
      } else {
        setErrors(prev => prev.concat(getApiErrors(configResp)));
      }
    } finally {
      setLoading(false);
    }
  }, [token, receiverUuid]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleChange = (key, type, raw) => {
    dirtyKeys.current.add(key);
    setFormValues(prev => ({ ...prev, [key]: raw }));
  };

  const buildPatch = () => {
    if (!schema) return {};
    const patch = {};
    // Compare each field against the layered "effective" value (receiver's
    // current state with any queued patch on top). A user edit only
    // contributes to the patch when it actually differs from that
    // baseline — avoids re-pushing values that are already in effect.
    const baseline = { ...(currentConfig || {}), ...serverConfig };
    schema.fields.forEach(f => {
      const cur = formValues[f.key];
      const prev = baseline[f.key];
      // valueType overrides type for enums whose VALUES are numeric: gain options are tenths
      // of a dB and the receiver parses that field as an Integer, so sending "445" fails there.
      const coerced = coerce(cur, f.valueType || f.type);
      if (!eq(coerced, prev)) {
        patch[f.key] = coerced;
      }
    });
    return patch;
  };

  const handleSave = async () => {
    setErrors([]);
    setInfo(null);
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      // Say what it compared against. "No changes to push" next to a field you just edited reads
      // as the form silently discarding the edit — which is exactly how this looked when a
      // receiver stopped applying config: the form kept repopulating from what the DEVICE last
      // reported, so every edit appeared to revert with no explanation.
      const stamp = serverMeta.currentConfigAt
        ? ` (as reported by the device at ${serverMeta.currentConfigAt})`
        : '';
      setInfo(`Nothing to push — every value already matches the receiver's current configuration${stamp}. `
        + `If you changed a field and see this, the value you chose is the one the device already has.`);
      return;
    }
    setSaving(true);
    try {
      const resp = await updateReceiverConfig(receiverUuid, patch, token);
      if (isApiSuccess(resp)) {
        // The edits are now the server's desired config, so let the reload show them from there.
        dirtyKeys.current.clear();
        setInfo(resp.pushed
          ? `Pushed v${resp.desiredVersion} to receiver.`
          : `Saved as v${resp.desiredVersion}; will push on next receiver connect.`);
        await loadAll();
      } else {
        setErrors(getApiErrors(resp));
      }
    } finally {
      setSaving(false);
    }
  };

  // Server-side auto-update opt-in. Optimistic on the checkbox so the UI feels immediate, but the
  // stored value is echoed back and wins — a failed write must not leave the box looking enabled.
  const handleAutoUpdateToggle = async (enabled) => {
    setErrors([]);
    setInfo(null);
    setSavingAutoUpdate(true);
    setServerMeta(m => ({ ...m, autoUpdate: enabled }));
    try {
      const resp = await setReceiverAutoUpdate(receiverUuid, enabled, token);
      if (isApiSuccess(resp)) {
        setServerMeta(m => ({ ...m, autoUpdate: !!resp.autoUpdate }));
        setInfo(resp.autoUpdate
          ? 'Auto-update on — this receiver will be updated automatically when a newer build is published for its platform.'
          : 'Auto-update off — updates only happen when you press Update receiver.');
      } else {
        setServerMeta(m => ({ ...m, autoUpdate: !enabled }));
        setErrors(getApiErrors(resp));
      }
    } catch (e) {
      setServerMeta(m => ({ ...m, autoUpdate: !enabled }));
      setErrors([e.message || 'Could not change auto-update']);
    } finally {
      setSavingAutoUpdate(false);
    }
  };

  // Stop polling on a terminal ack, or when the window expires — an update that dies
  // silently must not leave the page polling for ever.
  const [pollUntil, setPollUntil] = useState(0);

  // Terminal ack -> stop polling. Must live here with the other hooks: placed further down it
  // sits after an early return, and a conditionally-called hook breaks hook ordering.
  useEffect(() => {
    const st = serverMeta.updateStatus;
    if (st === 'OK' || st === 'NOOP' || st === 'ERR') setPollUntil(0);
  }, [serverMeta.updateStatus]);

  useEffect(() => {
    if (!pollUntil) return undefined;
    if (Date.now() > pollUntil) { setPollUntil(0); return undefined; }
    const id = setInterval(() => {
      if (Date.now() > pollUntil) { setPollUntil(0); return; }
      loadAll();
    }, UPDATE_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [pollUntil, loadAll]);

  const handlePushUpdate = async () => {
    setErrors([]);
    setInfo(null);
    setUpdating(true);
    try {
      const resp = await pushReceiverUpdate(receiverUuid, token);
      if (isApiSuccess(resp)) {
        setInfo(resp.pushed
          ? 'Update sent — waiting for the receiver to report progress…'
          : 'Update queued; will be sent on next receiver connect.');
        // Poll, rather than re-reading once. The receiver reports checking →
        // downloading N% → verifying → staged → restarting over what can be minutes on
        // a slow uplink; a single read 2 s later catches none of it, which is exactly
        // why this looked like nothing was happening.
        setTimeout(() => { loadAll(); }, 2000);
        setPollUntil(Date.now() + UPDATE_POLL_WINDOW_MS);
      } else {
        setErrors(getApiErrors(resp));
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleReset = () => {
    dirtyKeys.current.clear();
    setFormValues(toFormValues({ ...(currentConfig || {}), ...serverConfig }));
    setInfo(null);
    setErrors([]);
  };

  if (loading && !schema) {
    return <div className="cfg-form-loading">Loading config…</div>;
  }
  if (!schema) {
    return <div className="cfg-form-empty">Schema unavailable.</div>;
  }

  // Filter the schema by the current device so RTL-SDR users don't see
  // Airspy-specific knobs and vice versa. The `visibleFor` metadata on
  // each field lists which device values trigger visibility (empty /
  // null = always visible).
  //
  // Resolution order:
  //   1. The user's current form selection (responds live as they pick
  //      from the Preferred SDR dropdown).
  //   2. The persisted server config value.
  //   3. The auto-detected device the receiver is actually using right
  //      now (from STATS — e.g. "R820T" → RTL_SDR family). This step
  //      means a fleet of headless Pis baked in AUTO mode show their
  //      real-device knobs without anyone having to flip the dropdown.
  //   4. Fall back to 'AUTO' (= "everything").
  const preferred = (formValues.preferredDevice
                      ?? serverConfig.preferredDevice
                      ?? 'AUTO');
  const currentDevice = (preferred && preferred !== 'AUTO')
    ? preferred
    // Fall back to the hardware the owner declared on the setup page when the receiver is not
    // currently telling us. A receiver that loses its SDR reports deviceType "Unknown", which
    // resolves to no family and therefore to AUTO — and AUTO is in both rtlOnly and airspyOnly,
    // so the form showed the Airspy AND the RTL-SDR gain fields at once. We knew it was an
    // RTL-SDR the whole time; it was just stored somewhere this filter never looked.
    : deviceFamilyFromDetected(serverMeta.detectedDeviceType)
      || deviceFamilyFromDetected(declaredReceiverType)
      || 'AUTO';
  const visibleFields = schema.fields.filter(f =>
    !f.visibleFor || f.visibleFor.length === 0 || f.visibleFor.includes(currentDevice));
  const grouped = groupBySection({ ...schema, fields: visibleFields });
  const order = schema.sectionOrder || Object.keys(grouped);

  // Live receiver-reported identity. Shown only when STATS has actually
  // arrived; a freshly-claimed receiver that has never connected gets
  // the existing version-only header instead.
  const hasLiveSnapshot = !!(serverMeta.detectedDeviceType
                              || serverMeta.softwareVersion
                              || serverMeta.currentGainDb != null);
  const hasHostInfo = !!(serverMeta.hostName || serverMeta.hostModel
                          || serverMeta.osName || serverMeta.memTotalMb);
  const detectedFamily = deviceFamilyFromDetected(serverMeta.detectedDeviceType);
  // An update is "in progress" from the moment we send the push until the
  // receiver comes back reporting the new version. `updating` covers the brief
  // request; a server-side ACK of OK while the installed build is still behind
  // (updateAvailable still true) covers the download+restart window after that.
  // Three states, not two. A backend restart empties the live-socket registry,
  // the CFG-CURRENT snapshot and the STATS cache all at once, so for the ~90 s
  // until each receiver re-announces, a receiver streaming hundreds of messages
  // a second would render "NO" beside "Last Active: Just now" with a form full
  // of defaults. Saying "waiting" there is not a cosmetic nicety -- "NO" sends
  // the operator to debug a receiver that is working fine.
  //
  // Falls back to the old boolean when talking to a backend that doesn't send
  // connectionState yet, so an older server degrades to the previous behaviour
  // rather than rendering blank.
  const connState = (() => {
    const state = serverMeta.connectionState
      || (serverMeta.receiverConnected ? 'CONNECTED' : 'DISCONNECTED');
    if (state === 'CONNECTED') {
      return { state, label: 'YES', className: 'cfg-online', hint: null };
    }
    if (state === 'AWAITING_ANNOUNCE') {
      const secs = Math.max(0, Math.round((serverMeta.backendUptimeMs || 0) / 1000));
      return {
        state,
        label: 'WAITING',
        className: 'cfg-awaiting',
        hint: `— server restarted ${secs}s ago, receiver has not re-announced yet`,
      };
    }
    return { state, label: 'NO', className: 'cfg-offline', hint: null };
  })();

  // The form below is showing defaults, not the device's live settings, and in
  // this state that difference matters -- so say so rather than letting the
  // placeholders read as the device's configuration.
  const awaitingAnnounce = connState.state === 'AWAITING_ANNOUNCE';

  // Offline, but we still know what this receiver was doing.
  //
  // This is the case that used to render nothing at all. The identity and the device's own
  // CFG-CURRENT are persisted server-side (V085), so an unreachable receiver still has real
  // values to show — they are just no longer current. Say which, and stamp them; showing
  // last-known values unlabelled would be worse than showing nothing.
  const offline = connState.state === 'DISCONNECTED';
  const showingLastKnown = offline && (hasLiveSnapshot || !!serverMeta.currentConfigAt);

  // PROGRESS is the receiver actively working. OK/NOOP/ERR are terminal.
  const updatePhase = serverMeta.updateStatus === 'PROGRESS' ? serverMeta.updateDetail : null;
  const updateInProgress = updating
    || serverMeta.updateStatus === 'PROGRESS'
    || (serverMeta.updateStatus === 'OK' && serverMeta.updateAvailable);

  const notAviotrack = isAviotrack === false;

  return (
    <div className="cfg-form">
      {notAviotrack && (
        <RequiresAviotrackBanner
          latestVersion={latestReceiverVersion || serverMeta.latestVersion}
          onNavigate={onNavigate}
        />
      )}
      {showingLastKnown && !notAviotrack && (
        <div className="cfg-lastknown-banner" role="status">
          <strong>This receiver is offline — showing its last known state.</strong>{' '}
          {serverMeta.lastStatsAt && (
            <>It last reported {formatRelativeTime(serverMeta.lastStatsAt)}. </>
          )}
          {serverMeta.currentConfigAt
            ? <>The settings below are the configuration the device itself last reported
               ({formatRelativeTime(serverMeta.currentConfigAt)}), not defaults.{' '}</>
            : <>The device has never reported its own configuration, so the settings below are
               <em> defaults</em>, not its current values.{' '}</>}
          Changes you push are stored and applied the next time it reconnects.
        </div>
      )}
      {awaitingAnnounce && !notAviotrack && (
        <div className="cfg-awaiting-banner" role="status">
          <strong>Waiting for this receiver to report in.</strong>{' '}
          The server restarted recently and receivers re-announce themselves on
          reconnect, which usually takes under two minutes. Until then its live
          configuration, hardware and software version are unknown — the values
          below are <em>defaults</em>, not the device's current settings, and
          pushing now would send them to the device. This is not an indication
          that the receiver is down; check the message counts above.
        </div>
      )}
      <div className="cfg-form-meta">
        {hasLiveSnapshot && (
          <>
            <div className="cfg-meta-row">
              <span className="cfg-meta-label">Receiver hardware</span>
              <span className="cfg-meta-value">
                {serverMeta.detectedDeviceType || '—'}
                {detectedFamily && (
                  <span className="cfg-meta-hint"> ({familyLabel(detectedFamily)})</span>
                )}
              </span>
            </div>
            {/* ── Software version & update status ─────────────────── */}
            <div className="cfg-meta-row">
              <span className="cfg-meta-label">Receiver Installed Version</span>
              <span className="cfg-meta-value">
                {serverMeta.softwareVersion ? (
                  <>
                    v{serverMeta.softwareVersion}
                    {serverMeta.softwareName && (
                      <span className="cfg-meta-hint"> ({serverMeta.softwareName})</span>
                    )}
                  </>
                ) : '—'}
              </span>
            </div>
            <div className="cfg-meta-row cfg-meta-software">
              <span className="cfg-meta-label">Available Update Version</span>
              <span className="cfg-meta-value">
                {serverMeta.installerUpdateRequired ? (
                  <span className="cfg-update-badge"
                        title={`v${serverMeta.latestVersion} needs a newer installer` + (serverMeta.minInstallerVersion ? ` (≥ v${serverMeta.minInstallerVersion})` : '')}>
                    ⬆ v{serverMeta.latestVersion} — installer required
                  </span>
                ) : serverMeta.updateAvailable ? (
                  <span className="cfg-update-badge" title="A newer receiver build is available">
                    ⬆ v{serverMeta.latestVersion}
                  </span>
                ) : serverMeta.latestVersion ? (
                  <span className="cfg-update-uptodate" title="Running the latest build">Up to date</span>
                ) : '—'}
              </span>
              {serverMeta.updateAvailable && !updateInProgress && (
                <button
                  type="button"
                  className="cfg-btn cfg-btn-primary cfg-update-btn"
                  onClick={handlePushUpdate}
                  disabled={updating}
                >{updating ? 'Sending…' : 'Update receiver'}</button>
              )}
              {serverMeta.installerUpdateRequired && (
                <a
                  className="cfg-btn cfg-btn-primary cfg-update-btn"
                  href="/downloads"
                  onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate('downloads'); } }}
                  title={`This build can't be applied in place; run installer v${serverMeta.minInstallerVersion || ''} or newer`}
                >Download installer</a>
              )}
            </div>
            <div className="cfg-meta-row cfg-meta-software">
              <span className="cfg-meta-label">Automatic Updates</span>
              <span className="cfg-meta-value">
                <label className="cfg-autoupdate-toggle">
                  <input
                    type="checkbox"
                    checked={!!serverMeta.autoUpdate}
                    disabled={savingAutoUpdate}
                    onChange={e => handleAutoUpdateToggle(e.target.checked)}
                  />
                  <span>
                    {serverMeta.autoUpdate
                      ? 'On — updated automatically when a newer build is published for this receiver’s platform'
                      : 'Off — updates only when you press Update receiver'}
                  </span>
                </label>
              </span>
            </div>
            {serverMeta.installerUpdateRequired && (
              <div className="cfg-meta-row">
                <span className="cfg-meta-label"></span>
                <span className="cfg-meta-value cfg-meta-hint">
                  v{serverMeta.latestVersion} can't be applied as an in-place update
                  {serverMeta.minInstallerVersion ? ` (needs installer ≥ v${serverMeta.minInstallerVersion})` : ''}
                  {' '}— download and run the installer.
                </span>
              </div>
            )}
            {updateInProgress && (
              <div className="cfg-meta-row">
                <span className="cfg-meta-label">Updating to</span>
                <span className="cfg-meta-value cfg-updating">
                  v{serverMeta.latestVersion}
                  <span className="cfg-meta-hint">
                    {' '}({updatePhase ? describePhase(updatePhase) : 'contacting receiver…'})
                  </span>
                </span>
              </div>
            )}
            {serverMeta.updateStatus && !updateInProgress && (
              <div className="cfg-meta-row">
                <span className="cfg-meta-label">Last update</span>
                <span className={`cfg-meta-value ${serverMeta.updateStatus === 'ERR' ? 'cfg-err' : serverMeta.updateStatus === 'OK' ? 'cfg-ok' : ''}`}>
                  {serverMeta.updateStatus === 'OK' ? 'Completed'
                    : serverMeta.updateStatus === 'NOOP' ? 'Already up to date'
                    : serverMeta.updateStatus === 'ERR' ? 'Failed'
                    : serverMeta.updateStatus === 'PROGRESS' ? describePhase(serverMeta.updateDetail)
                    : serverMeta.updateStatus}
                </span>
              </div>
            )}
            {serverMeta.updateError && (
              <div className="cfg-meta-error">{serverMeta.updateError}</div>
            )}
            {/* ── Remote configuration sync version ────────────────── */}
            <div className="cfg-meta-row">
              <span className="cfg-meta-label">Config Version (target)</span>
              <span className="cfg-meta-value" title="Version of the config the server wants this receiver to run">
                v{serverMeta.desiredVersion}
              </span>
            </div>
            <div className="cfg-meta-row">
              <span className="cfg-meta-label">Config Version (applied)</span>
              <span className="cfg-meta-value" title="Config version the receiver has confirmed applying">
                v{serverMeta.appliedVersion}
                {serverMeta.desiredVersion > serverMeta.appliedVersion && (
                  <span className="cfg-meta-pending"> (pending)</span>
                )}
              </span>
            </div>
            <div className="cfg-meta-row">
              <span className="cfg-meta-label">Current gain</span>
              <span className="cfg-meta-value">
                {serverMeta.currentGainDb != null
                  ? serverMeta.currentGainDb.toFixed(1) + ' dB'
                  : '—'}
                <span className="cfg-meta-hint">
                  {' '}({formatGainMode(serverMeta.currentGainMode)})
                </span>
              </span>
            </div>
            {(serverMeta.sampleRateSps || serverMeta.centerFreqHz) && (
              <div className="cfg-meta-row">
                <span className="cfg-meta-label">SDR tuning</span>
                <span className="cfg-meta-value">
                  {formatFrequency(serverMeta.centerFreqHz) || '—'}
                  {serverMeta.sampleRateSps && (
                    <span className="cfg-meta-hint">
                      {' '}@ {formatSampleRate(serverMeta.sampleRateSps)}
                    </span>
                  )}
                </span>
              </div>
            )}
          </>
        )}
        {hasHostInfo && (
          <>
            <div className="cfg-meta-row">
              <span className="cfg-meta-label">Host</span>
              <span className="cfg-meta-value">
                {serverMeta.hostModel || '—'}
                {serverMeta.hostName && (
                  <span className="cfg-meta-hint"> ({serverMeta.hostName})</span>
                )}
              </span>
            </div>
            <div className="cfg-meta-row">
              <span className="cfg-meta-label">OS</span>
              <span className="cfg-meta-value">
                {serverMeta.osName || '—'}
                {serverMeta.kernel && (
                  <span className="cfg-meta-hint"> kernel {serverMeta.kernel}</span>
                )}
              </span>
            </div>
            <div className="cfg-meta-row">
              <span className="cfg-meta-label">Resources</span>
              <span className="cfg-meta-value">
                {serverMeta.memTotalMb
                  ? formatMem(serverMeta.memTotalMb) + ' RAM'
                  : '—'}
                {serverMeta.cpuCount && (
                  <span className="cfg-meta-hint">
                    {' · '}{serverMeta.cpuCount} CPU{serverMeta.cpuCount === 1 ? '' : 's'}
                    {serverMeta.arch && ' · ' + serverMeta.arch}
                  </span>
                )}
              </span>
            </div>
          </>
        )}
        {serverMeta.lastStatsAt && hasLiveSnapshot && (
          <div className="cfg-meta-row">
            <span className="cfg-meta-label">Snapshot</span>
            <span className="cfg-meta-value cfg-meta-time">
              {serverMeta.lastStatsAt}
              <span className="cfg-meta-hint">
                {' · '}{formatRelativeTime(serverMeta.lastStatsAt)}
              </span>
              {offline && <span className="cfg-stale-badge">last known</span>}
            </span>
          </div>
        )}
        {showingLastKnown && serverMeta.currentConfigAt && (
          <div className="cfg-meta-row">
            <span className="cfg-meta-label">Device config reported</span>
            <span className="cfg-meta-value cfg-meta-time">
              {serverMeta.currentConfigAt}
              <span className="cfg-meta-hint">
                {' · '}{formatRelativeTime(serverMeta.currentConfigAt)}
              </span>
            </span>
          </div>
        )}
        <div className="cfg-meta-row">
          <span className="cfg-meta-label">Receiver connected</span>
          <span className={`cfg-meta-value ${connState.className}`}>
            {connState.label}
            {connState.hint && (
              <span className="cfg-meta-hint"> {connState.hint}</span>
            )}
          </span>
        </div>
        {serverMeta.appliedStatus && (
          <div className="cfg-meta-row">
            <span className="cfg-meta-label">Last apply</span>
            <span className={`cfg-meta-value ${serverMeta.appliedStatus === 'OK' ? 'cfg-ok' : 'cfg-err'}`}>
              {serverMeta.appliedStatus}
              {serverMeta.appliedAt && <span className="cfg-meta-time"> @ {serverMeta.appliedAt}</span>}
            </span>
          </div>
        )}
        {serverMeta.appliedError && (
          <div className="cfg-meta-error">{serverMeta.appliedError}</div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="cfg-banner cfg-banner-error">
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}
      {info && (
        <div className="cfg-banner cfg-banner-info">{info}</div>
      )}

      {!notAviotrack && order.map(section => {
        const fields = grouped[section];
        if (!fields || fields.length === 0) return null;
        const collapsed = !!collapsedSections[section];
        return (
          <div key={section} className="cfg-section">
            <button
              type="button"
              className="cfg-section-header"
              onClick={() => setCollapsedSections(p => ({ ...p, [section]: !collapsed }))}
            >
              <span>{section}</span>
              <span className="cfg-section-toggle">{collapsed ? '▸' : '▾'}</span>
            </button>
            {!collapsed && (
              <div className="cfg-section-body">
                {fields.map(f => {
                  const dr = fieldDisabledReason(f, formValues);
                  return (
                    <FieldRow
                      key={f.key}
                      field={f}
                      value={formValues[f.key]}
                      onChange={handleChange}
                      disabled={!!dr}
                      disabledReason={dr}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {!notAviotrack && (
      <div className="cfg-form-actions">
        <button
          type="button"
          className="cfg-btn cfg-btn-secondary"
          onClick={handleReset}
          disabled={saving}
        >Reset</button>
        <button
          type="button"
          className="cfg-btn cfg-btn-primary"
          onClick={handleSave}
          disabled={saving}
          title={offline
            ? 'Receiver is offline — the configuration is stored and sent when it reconnects'
            : undefined}
        >{saving ? 'Saving…' : (offline ? 'Queue for receiver' : 'Push to receiver')}</button>
      </div>
      )}
    </div>
  );
}

function FieldRow({ field, value, onChange, disabled, disabledReason }) {
  const v = value === undefined || value === null ? '' : value;
  return (
    <label className={`cfg-field${disabled ? ' cfg-field-disabled' : ''}`}>
      <div className="cfg-field-label">
        <span>{field.label}</span>
        {field.requiresRestart && (
          <span className="cfg-restart-badge" title="Requires receiver restart">restart</span>
        )}
      </div>
      {renderInput(field, v, onChange, disabled)}
      {field.description && (
        <div className="cfg-field-help">
          {disabledReason && (
            <span className="cfg-field-disabled-reason">{disabledReason} · </span>
          )}
          {field.description}
        </div>
      )}
    </label>
  );
}

// Default-value placeholder for enums + free-text inputs. Surfaces the
// "what's effectively in use when this field is null" hint without
// auto-filling the field (we don't want to push the literal default
// string when the receiver's yaml has null — both produce identical
// runtime behaviour and pushing it would just bump the desired-version
// for no reason).
function defaultPlaceholder(field) {
  if (field.defaultValue == null || field.defaultValue === '') return '— default —';
  return `— default (${field.defaultValue}) —`;
}

// A bool the receiver reports as null is running at the schema default, so
// show THAT state — not an empty box. Before this a null rendered unchecked,
// indistinguishable from an explicit false: "Trust decoder ICAO whitelist"
// (null = ON) looked switched off on every receiver that had never set it,
// and "1-bit error correction" (null = ON since receiver 1.05.19) looked the
// same as the explicit false that actually was off on Gravediggers.
// The stored value stays null until the user clicks, so an untouched box
// never pushes the literal default (same rule as defaultPlaceholder).
function boolIsUnset(value) {
  return value === '' || value === null || value === undefined;
}

function effectiveBool(field, value) {
  if (boolIsUnset(value)) return field.defaultValue === 'true';
  return value === true || value === 'true';
}

function renderInput(field, value, onChange, disabled) {
  switch (field.type) {
    case 'bool':
      return (
        <span className="cfg-bool">
          <input
            type="checkbox"
            checked={effectiveBool(field, value)}
            disabled={disabled}
            onChange={e => onChange(field.key, field.type, e.target.checked)}
          />
          {boolIsUnset(value) && (
            <span
              className="cfg-default-badge"
              title={`Not set on the receiver — running at the default (${field.defaultValue === 'true' ? 'on' : 'off'})`}
            >
              default
            </span>
          )}
        </span>
      );
    case 'enum':
      return (
        <select
          value={value ?? ''}
          disabled={disabled}
          onChange={e => onChange(field.key, field.type, e.target.value || null)}
        >
          <option value="">{defaultPlaceholder(field)}</option>
          {/* enumLabels, when present, is positionally matched to enumValues: the option SENDS
              the wire value and SHOWS the human one. Gain needs this — the receiver wants tenths
              of a dB (445) and an operator thinks in dB (44.5). Showing the raw value invited
              exactly the mistake this replaced: a numeric field labelled "dB" that accepted 40
              and set the tuner to 4.0 dB. */}
          {(field.enumValues || []).map((opt, i) => (
            <option key={opt} value={opt}>{field.enumLabels?.[i] ?? opt}</option>
          ))}
        </select>
      );
    case 'int':
    case 'double':
      return (
        <input
          type="number"
          value={value ?? ''}
          step={field.type === 'int' ? 1 : 'any'}
          min={field.min ?? undefined}
          max={field.max ?? undefined}
          disabled={disabled}
          placeholder={field.defaultValue ? `default ${field.defaultValue}` : ''}
          onChange={e => onChange(field.key, field.type, e.target.value)}
        />
      );
    case 'string-list':
    case 'string':
    default:
      return (
        <input
          type="text"
          value={value ?? ''}
          disabled={disabled}
          placeholder={field.defaultValue ? `default ${field.defaultValue}` : ''}
          onChange={e => onChange(field.key, field.type, e.target.value)}
        />
      );
  }
}

function toFormValues(parsed) {
  const o = {};
  Object.keys(parsed || {}).forEach(k => { o[k] = parsed[k]; });
  return o;
}

// Server values, with the operator's unsaved edits laid back on top.
function withEdits(serverValues, prev, dirty) {
  dirty.forEach(k => { serverValues[k] = prev[k]; });
  return serverValues;
}

function coerce(raw, type) {
  if (raw === '' || raw === null || raw === undefined) return null;
  switch (type) {
    case 'int': {
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : null;
    }
    case 'double': {
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : null;
    }
    case 'bool':
      return raw === true || raw === 'true';
    default:
      return raw;
  }
}

function eq(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function groupBySection(schema) {
  const out = {};
  schema.fields.forEach(f => {
    const s = f.section || 'Other';
    if (!out[s]) out[s] = [];
    out[s].push(f);
  });
  return out;
}

// Per-field "disable when irrelevant" logic. Returns a short reason
// string when the field should be greyed out (the value isn't used by
// the receiver in the current parent-field state); returns null when
// the field is active. Kept here in the FE rather than encoded in the
// schema because the dependencies are stable and local — schema-driven
// dependency expressions would add complexity for no compounding
// benefit at this scale.
function fieldDisabledReason(field, values) {
  switch (field.key) {
    case 'rtlSdrGain': {
      const gm = values.gainMode;
      if (gm && gm !== 'MANUAL') {
        return `Inactive — gainMode is ${gm}`;
      }
      return null;
    }
    case 'airspyLinearityGain': {
      const gm = values.gainMode;
      if (gm && gm !== 'MANUAL') {
        return `Inactive — gainMode is ${gm}`;
      }
      return null;
    }
    case 'acarsFrequencies': {
      if (!values.acarsEnabled) {
        return 'Inactive — ACARS demodulator disabled';
      }
      return null;
    }
    case 'beastOutputPort': {
      if (!values.beastOutputEnabled) {
        return 'Inactive — Beast-Out server disabled';
      }
      return null;
    }
    default:
      return null;
  }
}

// Map the receiver-reported tuner / device tag (from STATS.device_type)
// to one of the schema's preferredDevice families. Falls back to null
// when we can't tell, in which case the caller leaves the filter at AUTO
// (= "everything visible"). Keep tag matching case-insensitive and
// substring-based so future librtlsdr / driver renames don't silently
// break visibility.
// Pretty label for a device-family code as shown in the header hint.
function familyLabel(family) {
  switch (family) {
    case 'RTL_SDR':  return 'RTL-SDR';
    case 'AIRSPY':   return 'Airspy';
    case 'BEAST_IN': return 'Beast-In';
    case 'AUTO':     return 'Auto';
    default:         return family;
  }
}

function deviceFamilyFromDetected(detected) {
  if (!detected) return null;
  const s = String(detected).toLowerCase();
  // Beast-In is reported as a literal "BEAST_IN" device_type by the
  // receiver when beast-in mode is active (no SDR is open in that mode).
  if (s.includes('beast')) return 'BEAST_IN';
  if (s.includes('airspy')) return 'AIRSPY';
  // RTL2832U-based dongles report their TUNER chip in device_type:
  //   r820t, r828d (most common), e4000, fc0012, fc0013, fc2580.
  if (s.includes('r820') || s.includes('r828') || s.includes('e4000')
      || s.includes('fc00') || s.includes('fc25')
      || s.includes('rtl')) return 'RTL_SDR';
  return null;
}

// Human-friendly display for the gain mode string the receiver reports
// (one of: MANUAL, AUTO_AVIOTRACK, AUTO_RTLSDR — anything else passes
// through unchanged so a future mode appears in the UI immediately).
function formatGainMode(mode) {
  if (!mode) return '—';
  switch (mode) {
    case 'MANUAL': return 'Manual';
    case 'AUTO_AVIOTRACK': return 'Auto (Aviotrack)';
    case 'AUTO_RTLSDR': return 'Auto (RTL-SDR AGC)';
    default: return mode;
  }
}

function formatSampleRate(sps) {
  if (sps == null) return null;
  // SPS values for ADS-B / VDL2 are typically 2.0–2.4 Msps. Show in MHz
  // with one decimal — enough resolution, none of the noise.
  return (Number(sps) / 1_000_000).toFixed(1) + ' Msps';
}

function formatFrequency(hz) {
  if (hz == null) return null;
  return (Number(hz) / 1_000_000).toFixed(3) + ' MHz';
}

// 8192 → "8 GB", 1024 → "1.0 GB", 512 → "512 MB". GB cuts in at 1024 MB.
function formatMem(mb) {
  if (mb == null) return null;
  const n = Number(mb);
  if (n >= 1024) {
    const gb = n / 1024;
    return (gb >= 8 ? gb.toFixed(0) : gb.toFixed(1)) + ' GB';
  }
  return n + ' MB';
}

export default ReceiverConfigForm;
